import history from 'history.js';
import { useAtomValue } from 'jotai';
import { Col } from 'reactstrap';

import '../styles.css';
import ComplianceChart from './ComplianceChart';
import ComplianceBar from './Components/ComplianceBar';
import { getDrillDownUrl } from './helper';
import {
  criticalityFiltersAtom,
  posturesByCriticalityChartDataAtom,
  topFailingPosturesDataAtom,
  topFailingTechnologiesDataAtom,
  topFailingCategoriesDataAtom,
  policyIdFilterAtom,
  appliedHostIdsAtom,
} from './state';
import BarOrDoughnutChart from '../components/BarOrDoughnutChart';
import DashboardDataGridChart from '../components/DashboardDataGridChart';
import { getSeverityFilterValues, getSeverityValue } from '../helper';
import { ChartType, ModalDialogData, Severity } from '../types';

// Define the type for drilldown parameters
interface GroupsDrilldownParams {
  cid?: number;
  status?: string;
  technology_id?: number;
  category?: string;
  criticality?: string;
}

interface Props {
  setModalComponentData: (data: ModalDialogData) => void;
}

const CurrentCharts = ({ setModalComponentData }: Props) => {
  const policyId = useAtomValue(policyIdFilterAtom);
  const appliedHostIds = useAtomValue(appliedHostIdsAtom);
  const severityFilters = useAtomValue(criticalityFiltersAtom);

  const posturesByCriticalityChartData = useAtomValue(
    posturesByCriticalityChartDataAtom,
  );
  const topFailingPosturesData = useAtomValue(topFailingPosturesDataAtom);
  const topFailingTechnologiesData = useAtomValue(
    topFailingTechnologiesDataAtom,
  );
  const topFailingCategoriesData = useAtomValue(topFailingCategoriesDataAtom);

  const handleExpandClick = (data: ModalDialogData | undefined) => {
    if (data) {
      setModalComponentData(data);
    }
  };

  // Function to generate drilldown URL based on parameters
  function getGroupsDrilldownUrl({
    cid,
    status,
    technology_id,
    category,
    criticality,
  }: GroupsDrilldownParams) {
    return getDrillDownUrl({
      policyId: policyId,
      hosts_in: appliedHostIds,
      cid: cid ? cid : undefined,
      status_in: status ? [status] : undefined,
      technology_id: technology_id ? technology_id : undefined,
      category: category ? category : undefined,
      criticality_in: criticality
        ? [getSeverityValue(criticality as Severity)]
        : getSeverityFilterValues(severityFilters),
    });
  }

  return (
    <>
      {/* Compliance Chart */}
      <Col sm="12" md="6" lg="3">
        <ComplianceChart
          passed={posturesByCriticalityChartData?.passedCount || 0}
          failed={posturesByCriticalityChartData?.failedCount || 0}
        />
      </Col>

      {/* Findings by Severity Chart */}
      <Col sm="12" md="6" lg="3">
        <BarOrDoughnutChart
          title="Findings by Severity"
          chartType={ChartType.BarStacked}
          datasets={posturesByCriticalityChartData?.datasets || []}
          tooltipLabels={['Passed', 'Failed']}
          labels={posturesByCriticalityChartData?.labels || []}
          severityFilters={severityFilters}
          colors={['#5FB3B3', '#B85050']}
          getDrillDownUrl={(label: string, sourceType: string): string => {
            return getGroupsDrilldownUrl({
              status: sourceType,
              criticality: label,
            });
          }}
        />
      </Col>

      {/* Top Failing Postures Data Grid */}
      {topFailingPosturesData && (
        <div className="col-md-6">
          <DashboardDataGridChart
            data={topFailingPosturesData}
            header={'Top Failing'}
            subHeader={'Group by Findings'}
            dataGridConfig={{
              rowIdKey: 'cid',
              columns: [
                {
                  key: 'cid',
                  label: 'CID',
                  onClick: (rowData) => {
                    history.push(getGroupsDrilldownUrl({ cid: rowData.cid }));
                  },
                },
                {
                  key: 'name',
                  label: 'Name',
                  enableCopyToClipboard: true,
                  width: '100%',
                  maxCharLength: 45,
                },
                {
                  key: 'criticality',
                  label: 'Severity',
                  classRules: [
                    {
                      className: 'text-serious',
                      ruleFunction: (rowData) =>
                        rowData.criticality === 'Serious',
                    },
                    {
                      className: 'text-medium',
                      ruleFunction: (rowData) =>
                        rowData.criticality === 'Medium',
                    },
                    {
                      className: 'text-urgent',
                      ruleFunction: (rowData) =>
                        rowData.criticality === 'Urgent',
                    },
                    {
                      className: 'text-critical',
                      ruleFunction: (rowData) =>
                        rowData.criticality === 'Critical',
                    },
                    {
                      className: 'text-minimal',
                      ruleFunction: (rowData) =>
                        rowData.criticality === 'Minimal',
                    },
                  ],
                },
                {
                  key: 'failed_count',
                  label: 'Failed',
                  onClick: (rowData) => {
                    history.push(
                      getGroupsDrilldownUrl({
                        cid: rowData.cid,
                        status: 'Failed',
                      }),
                    );
                  },
                },
                {
                  key: 'passed_count',
                  label: 'Passed',
                  onClick: (rowData) => {
                    history.push(
                      getGroupsDrilldownUrl({
                        cid: rowData.cid,
                        status: 'Passed',
                      }),
                    );
                  },
                },
                {
                  key: 'compliance',
                  label: 'Compliance',
                  width: '100px',
                  overrideContents: (rowData) => (
                    <ComplianceBar
                      passedCount={rowData.passed_count}
                      failedCount={rowData.failed_count}
                    />
                  ),
                },
              ],
            }}
            onExpandClick={handleExpandClick}
          />
        </div>
      )}

      {/* Top Failing Technologies Data Grid */}
      {topFailingTechnologiesData && (
        <div className="col-md-6">
          <DashboardDataGridChart
            data={topFailingTechnologiesData}
            header={'Top Failing'}
            subHeader={'Group By Technologies'}
            dataGridConfig={{
              rowIdKey: 'technology_id',
              columns: [
                {
                  key: 'name',
                  label: 'Technology',
                  enableCopyToClipboard: true,
                  width: '100%',
                  maxCharLength: 50,
                },
                {
                  key: 'failed_count',
                  label: 'Failed',
                  onClick: (rowData) => {
                    history.push(
                      getGroupsDrilldownUrl({
                        technology_id: rowData.technology_id,
                        status: 'Failed',
                      }),
                    );
                  },
                },
                {
                  key: 'passed_count',
                  label: 'Passed',
                  onClick: (rowData) => {
                    history.push(
                      getGroupsDrilldownUrl({
                        technology_id: rowData.technology_id,
                        status: 'Passed',
                      }),
                    );
                  },
                },
                {
                  key: 'compliance',
                  label: 'Compliance',
                  width: '100px',
                  overrideContents: (rowData) => (
                    <ComplianceBar
                      passedCount={rowData.passed_count}
                      failedCount={rowData.failed_count}
                    />
                  ),
                },
              ],
            }}
            onExpandClick={handleExpandClick}
          />
        </div>
      )}

      {/* Top Failing Categories Data Grid */}
      {topFailingCategoriesData && (
        <div className="col-md-6">
          <DashboardDataGridChart
            data={topFailingCategoriesData}
            header={'Top Failing'}
            subHeader={'Group By Categories'}
            dataGridConfig={{
              rowIdKey: 'category',
              columns: [
                {
                  key: 'category',
                  label: 'Category',
                  enableCopyToClipboard: true,
                  width: '100%',
                  maxCharLength: 50,
                },
                {
                  key: 'failed_count',
                  label: 'Failed',
                  onClick: (rowData) => {
                    history.push(
                      getGroupsDrilldownUrl({
                        category: rowData.category,
                        status: 'Failed',
                      }),
                    );
                  },
                },
                {
                  key: 'passed_count',
                  label: 'Passed',
                  onClick: (rowData) => {
                    history.push(
                      getGroupsDrilldownUrl({
                        category: rowData.category,
                        status: 'Passed',
                      }),
                    );
                  },
                },
                {
                  key: 'compliance',
                  label: 'Compliance',
                  width: '100px',
                  overrideContents: (rowData) => (
                    <ComplianceBar
                      passedCount={rowData.passed_count}
                      failedCount={rowData.failed_count}
                    />
                  ),
                },
              ],
            }}
            onExpandClick={handleExpandClick}
          />
        </div>
      )}
    </>
  );
};

export default CurrentCharts;
